'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface TypewriterEffectProps {
  strings: string[];
  speed?: number;
  deleteSpeed?: number;
  loop?: boolean;
  pauseTime?: number;
  className?: string;
}

const TypewriterEffect: React.FC<TypewriterEffectProps> = ({
  strings,
  speed = 50,
  deleteSpeed = 30,
  loop = true,
  pauseTime = 2000,
  className = '',
}) => {
  const [currentStringIndex, setCurrentStringIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseTime);
      return () => clearTimeout(pauseTimer);
    }

    const currentString = strings[currentStringIndex] || '';
    const timer = setTimeout(() => {
      if (!isDeleting) {
        if (currentText.length < currentString.length) {
          setCurrentText(currentString.slice(0, currentText.length + 1));
        } else {
          setIsPaused(true);
        }
      } else {
        if (currentText.length > 0) {
          setCurrentText(currentText.slice(0, -1));
        } else {
          setIsDeleting(false);
          if (loop || currentStringIndex < strings.length - 1) {
            setCurrentStringIndex((prev) => (prev + 1) % strings.length);
          }
        }
      }
    }, isDeleting ? deleteSpeed : speed);

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, isPaused, currentStringIndex, strings, speed, deleteSpeed, pauseTime, loop]);

  return (
    <span data-component-id="src/components/motion/TypewriterEffect.tsx:61:4" data-component-path="src/components/motion/TypewriterEffect.tsx" data-component-line="61" data-component-end-line="72" data-component-file="TypewriterEffect.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" className={className}>
      {currentText}
      <motion.span data-component-id="src/components/motion/TypewriterEffect.tsx:63:6" data-component-path="src/components/motion/TypewriterEffect.tsx" data-component-line="63" data-component-end-line="71" data-component-file="TypewriterEffect.tsx" data-component-name="motion.span" data-component-content="%7B%22elementName%22%3A%22motion.span%22%2C%22className%22%3A%22inline-block%20w-0.5%20h-%5B1em%5D%20bg-current%20ml-1%20align-middle%22%7D"
        animate={{ opacity: [1, 0] }}
        transition={{
          duration: 0.8,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
        className="inline-block w-0.5 h-[1em] bg-current ml-1 align-middle"
      />
    </span>
  );
};

export default TypewriterEffect;
